/**
 * PROJECT PLAN TAB
 *
 * Manages dependencies, milestones, and scheduling for a project.
 * Shows all project children (jobs, tasks, milestones) and their
 * dependency relationships (depends_on, blocks) so project managers
 * know the critical path and can assign responsibility.
 *
 * Features:
 * - Visual dependency chain (what must finish before what can start)
 * - Milestone timeline with due dates and status
 * - Create/remove dependencies between project children
 * - Assign responsibility (owner) to each dependency
 * - Dependency health indicators (blocked, overdue predecessor, etc.)
 *
 * @date March 2026
 */

'use client';

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  GitBranch, Plus, Search, X, ArrowRight, Loader2, RefreshCw,
  ChevronDown, ChevronRight, ExternalLink, AlertCircle, CheckCircle,
  Clock, Flag, Milestone, Link2, Unlink, ArrowDown, Users,
  CalendarDays, Shield, AlertTriangle, CircleDot
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import { notify, confirm } from '@/lib/notifications';
import WorkItemModal from '@/components/WorkItemModal';

// ============================================================================
// TYPES
// ============================================================================

interface ProjectChild {
  id: string;
  item_number: string;
  item_type: string;
  item_subtype?: string;
  title: string;
  status: string;
  priority?: string;
  assigned_to?: number | null;
  assignee_name?: string | null;
  start_date?: string | null;
  due_date?: string | null;
  estimated_hours?: number;
  actual_hours?: number;
}

interface Dependency {
  relationship_id: string;
  source: ProjectChild;       // the item that depends on another
  target: ProjectChild;       // the item that must be completed first
  relationship_type: string;  // depends_on, blocks
  notes?: string;
  created_at?: string;
  metadata?: Record<string, any>;
}

interface MilestoneItem extends ProjectChild {
  progress_percentage?: number;
  linked_items_count?: number;
}

interface TeamMember {
  userId: number;
  display_name: string;
}

interface ProjectPlanTabProps {
  workItemId: string;
  projectTitle?: string;
  isActive: boolean;
  onUpdate?: () => void;
  onOpenItem?: (itemId: string) => void;
}

// ============================================================================
// HELPERS
// ============================================================================

const getHeaders = (): Record<string, string> => {
  const tokens = authService.getTokens();
  return {
    'Content-Type': 'application/json',
    ...(tokens?.access_token ? { Authorization: `Bearer ${tokens.access_token}` } : {}),
  };
};

const STATUS_STYLES: Record<string, { bg: string; text: string; dot: string }> = {
  open:        { bg: 'bg-blue-100 dark:bg-blue-900/40',     text: 'text-blue-800 dark:text-blue-300',     dot: 'bg-blue-500' },
  planning:    { bg: 'bg-slate-100 dark:bg-slate-900/40',   text: 'text-slate-800 dark:text-slate-300',   dot: 'bg-slate-500' },
  active:      { bg: 'bg-cyan-100 dark:bg-cyan-900/40',     text: 'text-cyan-800 dark:text-cyan-300',     dot: 'bg-cyan-500' },
  assigned:    { bg: 'bg-indigo-100 dark:bg-indigo-900/40', text: 'text-indigo-800 dark:text-indigo-300', dot: 'bg-indigo-500' },
  scheduled:   { bg: 'bg-cyan-100 dark:bg-cyan-900/40',     text: 'text-cyan-800 dark:text-cyan-300',     dot: 'bg-cyan-500' },
  in_progress: { bg: 'bg-yellow-100 dark:bg-yellow-900/40', text: 'text-yellow-800 dark:text-yellow-300', dot: 'bg-yellow-500' },
  on_hold:     { bg: 'bg-orange-100 dark:bg-orange-900/40', text: 'text-orange-800 dark:text-orange-300', dot: 'bg-orange-500' },
  completed:   { bg: 'bg-green-100 dark:bg-green-900/40',   text: 'text-green-800 dark:text-green-300',   dot: 'bg-green-500' },
  done:        { bg: 'bg-green-100 dark:bg-green-900/40',   text: 'text-green-800 dark:text-green-300',   dot: 'bg-green-500' },
  cancelled:   { bg: 'bg-red-100 dark:bg-red-900/40',       text: 'text-red-800 dark:text-red-300',       dot: 'bg-red-500' },
};

const getStatus = (s: string) =>
  STATUS_STYLES[s] || { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-700 dark:text-gray-300', dot: 'bg-gray-400' };

const TYPE_COLORS: Record<string, string> = {
  job: 'text-blue-500',
  task: 'text-indigo-500',
  milestone: 'text-amber-500',
  ticket: 'text-violet-500',
};

const TYPE_BADGES: Record<string, { bg: string; text: string }> = {
  job:       { bg: 'bg-blue-100 dark:bg-blue-900/30',   text: 'text-blue-700 dark:text-blue-400' },
  task:      { bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-700 dark:text-indigo-400' },
  milestone: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400' },
  ticket:    { bg: 'bg-violet-100 dark:bg-violet-900/30', text: 'text-violet-700 dark:text-violet-400' },
};

const isTerminal = (status: string) =>
  ['completed', 'done', 'closed', 'cancelled', 'invoiced'].includes(status);

const formatDate = (d: string | null | undefined) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
};

const daysUntil = (d: string | null | undefined) => {
  if (!d) return null;
  const diff = Math.ceil((new Date(d).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  return diff;
};

// ============================================================================
// COMPONENT
// ============================================================================

export default function ProjectPlanTab({
  workItemId, projectTitle, isActive, onUpdate, onOpenItem,
}: ProjectPlanTabProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';

  // Data
  const [children, setChildren] = useState<ProjectChild[]>([]);
  const [dependencies, setDependencies] = useState<Dependency[]>([]);
  const [milestones, setMilestones] = useState<MilestoneItem[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);

  // UI state
  const [activeSection, setActiveSection] = useState<'dependencies' | 'milestones' | 'schedule'>('dependencies');
  const [showAddDep, setShowAddDep] = useState(false);
  const [showAddMilestone, setShowAddMilestone] = useState(false);
  const [depSource, setDepSource] = useState<string>(''); // item that depends on...
  const [depTarget, setDepTarget] = useState<string>(''); // ...this item (must complete first)
  const [depNotes, setDepNotes] = useState('');
  const [creating, setCreating] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [milestoneSearch, setMilestoneSearch] = useState('');
  const [milestoneResults, setMilestoneResults] = useState<ProjectChild[]>([]);
  const [searchingMilestones, setSearchingMilestones] = useState(false);
  const [linkingMilestone, setLinkingMilestone] = useState<string | null>(null);
  const [showCreateMilestone, setShowCreateMilestone] = useState(false);
  const [autoScheduling, setAutoScheduling] = useState(false);

  const hasLoadedRef = useRef(false);
  const msSearchTimeout = useRef<NodeJS.Timeout | null>(null);

  // ── Load project children (jobs, tasks, milestones linked to project) ──
  const loadChildren = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/work-item-relationships', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ action: 'getHierarchy', workItemId }),
      });
      const data = await res.json();
      if (data.success) {
        const all: ProjectChild[] = (data.data?.children || []);
        setChildren(all);
        setMilestones(all.filter(c => c.item_type === 'milestone'));
      }
    } catch (err) {
      console.error('[ProjectPlanTab] Error loading children:', err);
    }
  }, [workItemId]);

  // ── Load dependency relationships between project children ──
  const loadDependencies = useCallback(async () => {
    try {
      // Get relationships for the project to find dependency chains
      const res = await fetch(`/api/v1/work-items/${workItemId}/relationships`, {
        headers: getHeaders(),
      });
      const data = await res.json();
      if (!data.success) return;

      const rels = data.data?.relationships || {};
      const outgoing = rels.outgoing || {};
      const incoming = rels.incoming || {};

      // Now load relationships for each child to find inter-child dependencies
      // This is done by loading children first, then querying each child's relationships
      // We'll batch this with a dedicated approach
    } catch (err) {
      console.error('[ProjectPlanTab] Error loading dependencies:', err);
    }
  }, [workItemId]);

  // ── Load all data ──
  const loadAll = useCallback(async () => {
    if (!isActive) return;
    setLoading(true);
    try {
      // 1. Load project children
      const childRes = await fetch('/api/v1/work-item-relationships', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ action: 'getHierarchy', workItemId }),
      });
      const childData = await childRes.json();
      let allChildren: ProjectChild[] = [];
      if (childData.success) {
        allChildren = childData.data?.children || [];
        setChildren(allChildren);
        setMilestones(allChildren.filter(c => c.item_type === 'milestone'));
      }

      // 2. Load dependencies between children
      // For each child, get their outgoing depends_on / blocks relationships
      // Use Promise.all for parallel fetching (batch of children)
      const deps: Dependency[] = [];
      const childIds = new Set(allChildren.map(c => c.id));

      // We'll fetch relationships for all children in parallel
      const relPromises = allChildren.map(async (child) => {
        try {
          const relRes = await fetch(`/api/v1/work-items/${child.id}/relationships`, {
            headers: getHeaders(),
          });
          const relData = await relRes.json();
          if (!relData.success) return;

          const outgoing = relData.data?.relationships?.outgoing || {};

          // depends_on: child depends on target (target must complete first)
          const dependsOn = outgoing['depends_on'] || outgoing['depends-on'] || [];
          for (const rel of dependsOn) {
            // Only include if target is also a project child
            if (rel.item && childIds.has(rel.item.id)) {
              deps.push({
                relationship_id: rel.relationship_id,
                source: child,
                target: {
                  id: rel.item.id,
                  item_number: rel.item.item_number,
                  item_type: rel.item.item_type,
                  title: rel.item.title,
                  status: rel.item.status,
                  priority: rel.item.priority,
                  assigned_to: rel.item.assigned_to,
                  due_date: rel.item.due_date,
                },
                relationship_type: 'depends_on',
                notes: rel.notes,
                created_at: rel.created_at,
                metadata: rel.metadata,
              });
            }
          }

          // blocks: child blocks target (child must complete before target can start)
          const blocks = outgoing['blocks'] || [];
          for (const rel of blocks) {
            if (rel.item && childIds.has(rel.item.id)) {
              deps.push({
                relationship_id: rel.relationship_id,
                source: child,     // blocker
                target: {          // blocked item
                  id: rel.item.id,
                  item_number: rel.item.item_number,
                  item_type: rel.item.item_type,
                  title: rel.item.title,
                  status: rel.item.status,
                  priority: rel.item.priority,
                  assigned_to: rel.item.assigned_to,
                  due_date: rel.item.due_date,
                },
                relationship_type: 'blocks',
                notes: rel.notes,
                created_at: rel.created_at,
                metadata: rel.metadata,
              });
            }
          }
        } catch {}
      });

      await Promise.all(relPromises);
      setDependencies(deps);

      // 3. Load team members for responsibility assignment
      try {
        const tmRes = await fetch('/api/v1/module-access/users?module_key=total-operations&include_operations_role=true', {
          headers: getHeaders(),
        });
        const tmData = await tmRes.json();
        if (tmData.success) {
          setTeamMembers((tmData.data || []).map((u: any) => ({
            userId: u.userId || u.id,
            display_name: u.display_name || u.name || u.email || `User ${u.userId || u.id}`,
          })));
        }
      } catch {}

    } catch (err) {
      console.error('[ProjectPlanTab] Error loading plan data:', err);
    } finally {
      setLoading(false);
    }
  }, [workItemId, isActive]);

  useEffect(() => {
    if (isActive && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadAll();
    }
  }, [isActive, loadAll]);

  // ── Create a dependency ──
  const createDependency = useCallback(async () => {
    if (!depSource || !depTarget || depSource === depTarget) {
      notify.error('Please select two different items');
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`/api/v1/work-items/${depSource}/relationships`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          target_item_id: depTarget,
          relationship_type: 'depends_on',
          notes: depNotes || undefined,
        }),
      });
      const data = await res.json();
      if (data.success) {
        notify.success('Dependency created');
        setShowAddDep(false);
        setDepSource('');
        setDepTarget('');
        setDepNotes('');
        hasLoadedRef.current = false;
        loadAll();
        onUpdate?.();
      } else {
        notify.error(data.error || 'Failed to create dependency');
      }
    } catch (err) {
      notify.error('Failed to create dependency');
    } finally {
      setCreating(false);
    }
  }, [depSource, depTarget, depNotes, loadAll, onUpdate]);

  // ── Remove a dependency ──
  const removeDependency = useCallback(async (dep: Dependency) => {
    const ok = await confirm({ message: `Remove dependency: "${dep.source.title}" depends on "${dep.target.title}"?` });
    if (!ok) return;
    setRemovingId(dep.relationship_id);
    try {
      const res = await fetch(`/api/v1/work-items/${dep.source.id}/relationships/${dep.relationship_id}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        notify.success('Dependency removed');
        setDependencies(prev => prev.filter(d => d.relationship_id !== dep.relationship_id));
        onUpdate?.();
      } else {
        notify.error(data.error || 'Failed to remove');
      }
    } catch {
      notify.error('Failed to remove dependency');
    } finally {
      setRemovingId(null);
    }
  }, [onUpdate]);

  // ── Create a new milestone and link to project ──
  const handleCreateMilestone = useCallback(async (itemData: Record<string, any>) => {
    try {
      const res = await fetch('/api/v1/work-items', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          ...itemData,
          item_type: 'milestone',
          module_context: 'project-management',
        }),
      });
      const data = await res.json();
      if (!data.success) {
        notify.error(data.error || 'Failed to create milestone');
        return;
      }
      const newId = data.data?.id;
      if (newId) {
        // Auto-link the new milestone to this project
        await fetch('/api/v1/work-item-relationships', {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify({
            action: 'create',
            sourceItemId: newId,
            targetItemId: workItemId,
            relationshipType: 'child_of',
          }),
        });
      }
      notify.success('Milestone created and linked');
      setShowCreateMilestone(false);
      hasLoadedRef.current = false;
      loadAll();
      onUpdate?.();
    } catch {
      notify.error('Failed to create milestone');
    }
  }, [workItemId, loadAll, onUpdate]);

  // ── Search milestones to link ──
  const searchMilestones = useCallback(async (query: string) => {
    if (query.length < 2) { setMilestoneResults([]); return; }
    setSearchingMilestones(true);
    try {
      const params = new URLSearchParams({ item_type: 'milestone', search: query, limit: '20' });
      const res = await fetch(`/api/v1/work-items?${params}`, { headers: getHeaders() });
      const data = await res.json();
      if (data.success) {
        const items = Array.isArray(data.data) ? data.data : data.data?.items || [];
        // Exclude already linked milestones
        const linkedIds = new Set(milestones.map(m => m.id));
        setMilestoneResults(items.filter((i: any) => !linkedIds.has(i.id)));
      }
    } catch {} finally { setSearchingMilestones(false); }
  }, [milestones]);

  const handleMilestoneSearchChange = (value: string) => {
    setMilestoneSearch(value);
    if (msSearchTimeout.current) clearTimeout(msSearchTimeout.current);
    msSearchTimeout.current = setTimeout(() => searchMilestones(value), 300);
  };

  // ── Link a milestone to this project ──
  const linkMilestone = useCallback(async (milestoneId: string) => {
    setLinkingMilestone(milestoneId);
    try {
      const res = await fetch('/api/v1/work-item-relationships', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          action: 'create',
          sourceItemId: milestoneId,
          targetItemId: workItemId,
          relationshipType: 'child_of',
        }),
      });
      const data = await res.json();
      if (data.success) {
        notify.success('Milestone linked');
        setShowAddMilestone(false);
        setMilestoneSearch('');
        setMilestoneResults([]);
        hasLoadedRef.current = false;
        loadAll();
        onUpdate?.();
      } else {
        notify.error(data.error || 'Failed to link milestone');
      }
    } catch {
      notify.error('Failed to link milestone');
    } finally {
      setLinkingMilestone(null);
    }
  }, [workItemId, loadAll, onUpdate]);

  // ── Unlink a milestone from this project ──
  const unlinkMilestone = useCallback(async (ms: MilestoneItem) => {
    const ok = await confirm({ message: `Unlink milestone "${ms.title}" from this project?` });
    if (!ok) return;
    setRemovingId(ms.id);
    try {
      // Find the child_of relationship between this milestone and the project
      const relRes = await fetch(`/api/v1/work-items/${ms.id}/relationships`, { headers: getHeaders() });
      const relData = await relRes.json();
      const childOfRels = relData.data?.relationships?.outgoing?.['child_of'] || [];
      const rel = childOfRels.find((r: any) => r.item?.id === workItemId);
      if (!rel) {
        notify.error('Relationship not found');
        return;
      }
      const res = await fetch(`/api/v1/work-items/${ms.id}/relationships/${rel.relationship_id}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        notify.success('Milestone unlinked');
        setMilestones(prev => prev.filter(m => m.id !== ms.id));
        setChildren(prev => prev.filter(c => c.id !== ms.id));
        onUpdate?.();
      } else {
        notify.error(data.error || 'Failed to unlink');
      }
    } catch {
      notify.error('Failed to unlink milestone');
    } finally {
      setRemovingId(null);
    }
  }, [workItemId, onUpdate]);

  // ── Auto-schedule: push dates forward based on dependency chains ──
  const autoSchedule = useCallback(async () => {
    if (dependencies.length === 0) {
      notify.error('No dependencies to schedule from');
      return;
    }

    const ok = await confirm({ message: 'Auto-schedule will adjust start/due dates of dependent items so they start after their prerequisites finish. Continue?' });
    if (!ok) return;

    setAutoScheduling(true);
    try {
      // Build predecessor map: sourceId -> [predecessor items]
      const predecessorMap = new Map<string, ProjectChild[]>();
      for (const dep of dependencies) {
        if (!predecessorMap.has(dep.source.id)) predecessorMap.set(dep.source.id, []);
        predecessorMap.get(dep.source.id)!.push(dep.target);
      }

      // Topological sort via Kahn's
      const childMap = new Map(children.map(c => [c.id, c]));
      const inDeg = new Map<string, number>();
      for (const c of children) inDeg.set(c.id, 0);
      for (const dep of dependencies) {
        inDeg.set(dep.source.id, (inDeg.get(dep.source.id) || 0) + 1);
      }

      const queue: string[] = [];
      for (const [id, deg] of inDeg) {
        if (deg === 0) queue.push(id);
      }

      const topoOrder: string[] = [];
      while (queue.length > 0) {
        const id = queue.shift()!;
        topoOrder.push(id);
        for (const dep of dependencies) {
          if (dep.target.id === id) {
            const nd = (inDeg.get(dep.source.id) || 1) - 1;
            inDeg.set(dep.source.id, nd);
            if (nd === 0) queue.push(dep.source.id);
          }
        }
      }

      const today = new Date();
      const updates: { id: string; start_date: string; due_date: string }[] = [];

      for (const id of topoOrder) {
        const item = childMap.get(id);
        if (!item || isTerminal(item.status)) continue;

        const preds = predecessorMap.get(id) || [];
        if (preds.length === 0) continue; // Don't move root items

        // Earliest start = max of all predecessors' due dates + 1 day
        let maxPredEnd = new Date(0);
        for (const pred of preds) {
          const predItem = childMap.get(pred.id);
          const predEnd = predItem?.due_date ? new Date(predItem.due_date) : today;
          if (predEnd > maxPredEnd) maxPredEnd = predEnd;
        }
        const newStart = new Date(maxPredEnd.getTime() + 24 * 60 * 60 * 1000);
        const estHours = item.estimated_hours || 8;
        const durationDays = Math.max(1, Math.ceil(estHours / 8));
        const newEnd = new Date(newStart.getTime() + durationDays * 24 * 60 * 60 * 1000);

        updates.push({
          id,
          start_date: newStart.toISOString().split('T')[0],
          due_date: newEnd.toISOString().split('T')[0],
        });
      }

      if (updates.length === 0) {
        notify.info('All items are already correctly scheduled');
        setAutoScheduling(false);
        return;
      }

      // Batch update all items
      for (const upd of updates) {
        await fetch('/api/v1/work-items', {
          method: 'PUT',
          headers: getHeaders(),
          body: JSON.stringify({
            ids: [upd.id],
            updates: { start_date: upd.start_date, due_date: upd.due_date },
          }),
        });
      }

      notify.success(`Auto-scheduled ${updates.length} item(s) based on dependencies`);
      hasLoadedRef.current = false;
      loadAll();
      onUpdate?.();
    } catch (err) {
      console.error('[ProjectPlanTab] Auto-schedule error:', err);
      notify.error('Failed to auto-schedule');
    } finally {
      setAutoScheduling(false);
    }
  }, [dependencies, children, loadAll, onUpdate]);

  // ── Derived: dependency health analysis ──
  const depHealth = useMemo(() => {
    return dependencies.map(dep => {
      const targetDone = isTerminal(dep.target.status);
      const sourceDone = isTerminal(dep.source.status);
      const targetOverdue = dep.target.due_date && daysUntil(dep.target.due_date) !== null && daysUntil(dep.target.due_date)! < 0;
      const sourceBlocked = !targetDone && !sourceDone;

      let health: 'clear' | 'blocked' | 'at_risk' | 'resolved' = 'clear';
      if (sourceDone && targetDone) health = 'resolved';
      else if (sourceBlocked && targetOverdue) health = 'at_risk';
      else if (sourceBlocked) health = 'blocked';

      return { ...dep, health, targetDone, sourceDone, targetOverdue };
    });
  }, [dependencies]);

  // ── Derived: milestone timeline ──
  const sortedMilestones = useMemo(() => {
    return [...milestones].sort((a, b) => {
      if (!a.due_date && !b.due_date) return 0;
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
    });
  }, [milestones]);

  // Non-milestone children for the dependency dropdowns
  const depCandidates = useMemo(() =>
    children.filter(c => c.item_type !== 'milestone'),
  [children]);

  // ── Render ──

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
        <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading project plan...</span>
      </div>
    );
  }

  if (children.length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center py-16 gap-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
        <GitBranch className="w-10 h-10 opacity-30" />
        <div className="text-center">
          <p className="font-medium">No items in this project yet</p>
          <p className="text-xs mt-1">Add jobs, tasks, or milestones to the project first, then define dependencies here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ── Section Tabs ── */}
      <div className={`flex items-center gap-1 p-1 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
        <button
          onClick={() => setActiveSection('dependencies')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
            activeSection === 'dependencies'
              ? isDark ? 'bg-gray-700 text-white shadow' : 'bg-white text-gray-900 shadow'
              : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <GitBranch className="w-4 h-4" />
          Dependencies
          {dependencies.length > 0 && (
            <span className={`px-1.5 py-0.5 text-xs rounded-full ${
              activeSection === 'dependencies'
                ? 'bg-blue-500 text-white'
                : isDark ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
            }`}>{dependencies.length}</span>
          )}
        </button>
        <button
          onClick={() => setActiveSection('milestones')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
            activeSection === 'milestones'
              ? isDark ? 'bg-gray-700 text-white shadow' : 'bg-white text-gray-900 shadow'
              : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Flag className="w-4 h-4" />
          Milestones
          {milestones.length > 0 && (
            <span className={`px-1.5 py-0.5 text-xs rounded-full ${
              activeSection === 'milestones'
                ? 'bg-amber-500 text-white'
                : isDark ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
            }`}>{milestones.length}</span>
          )}
        </button>
        <button
          onClick={() => setActiveSection('schedule')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
            activeSection === 'schedule'
              ? isDark ? 'bg-gray-700 text-white shadow' : 'bg-white text-gray-900 shadow'
              : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <CalendarDays className="w-4 h-4" />
          Schedule
        </button>
      </div>

      {/* ══════════════════ DEPENDENCIES SECTION ══════════════════ */}
      {activeSection === 'dependencies' && (
        <div className="space-y-3">
          {/* Header + Add Button */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                Dependency Chain
              </h3>
              <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Define which items must complete before others can start
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => { hasLoadedRef.current = false; loadAll(); }}
                className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                title="Refresh">
                <RefreshCw className="w-4 h-4" />
              </button>
              <button onClick={() => setShowAddDep(!showAddDep)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                <Plus className="w-3.5 h-3.5" />
                Add Dependency
              </button>
            </div>
          </div>

          {/* Add Dependency Form */}
          {showAddDep && (
            <div className={`p-4 rounded-xl border-2 border-dashed ${isDark ? 'border-blue-700 bg-blue-900/10' : 'border-blue-300 bg-blue-50'}`}>
              <div className="flex items-center gap-2 mb-3">
                <GitBranch className="w-4 h-4 text-blue-500" />
                <span className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  New Dependency
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 items-end">
                {/* Source: the item that depends on the target */}
                <div>
                  <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    This item...
                  </label>
                  <select value={depSource} onChange={(e) => setDepSource(e.target.value)}
                    className={`w-full px-3 py-2 text-sm border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}>
                    <option value="">Select item...</option>
                    {depCandidates.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.item_number} — {c.title} ({c.item_type})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Arrow */}
                <div className="hidden sm:flex items-center justify-center pt-4">
                  <div className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                    depends on
                    <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
                <div className="sm:hidden flex items-center justify-center">
                  <ArrowDown className="w-4 h-4 text-gray-400" />
                  <span className={`text-xs ml-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>depends on</span>
                </div>

                {/* Target: the item that must be completed first */}
                <div>
                  <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    ...must wait for
                  </label>
                  <select value={depTarget} onChange={(e) => setDepTarget(e.target.value)}
                    className={`w-full px-3 py-2 text-sm border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}>
                    <option value="">Select prerequisite...</option>
                    {depCandidates.filter(c => c.id !== depSource).map(c => (
                      <option key={c.id} value={c.id}>
                        {c.item_number} — {c.title} ({c.item_type})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Notes */}
              <div className="mt-3">
                <input type="text" value={depNotes} onChange={(e) => setDepNotes(e.target.value)}
                  placeholder="Notes (optional) — e.g. 'Electrical work must pass inspection first'"
                  className={`w-full px-3 py-2 text-sm border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`} />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-2 mt-3">
                <button onClick={() => { setShowAddDep(false); setDepSource(''); setDepTarget(''); setDepNotes(''); }}
                  className={`px-3 py-1.5 text-sm rounded-lg ${isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}>
                  Cancel
                </button>
                <button onClick={createDependency} disabled={creating || !depSource || !depTarget}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
                  {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                  Create
                </button>
              </div>
            </div>
          )}

          {/* Dependency List */}
          {depHealth.length === 0 ? (
            <div className={`text-center py-10 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              <GitBranch className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No dependencies defined</p>
              <p className="text-xs mt-1">Click "Add Dependency" to define the order items must be completed</p>
            </div>
          ) : (
            <div className="space-y-2">
              {depHealth.map(dep => {
                const ss = getStatus(dep.target.status);
                const ds = getStatus(dep.source.status);
                const targetBadge = TYPE_BADGES[dep.target.item_type] || TYPE_BADGES.task;
                const sourceBadge = TYPE_BADGES[dep.source.item_type] || TYPE_BADGES.task;
                const isRemoving = removingId === dep.relationship_id;

                return (
                  <div key={dep.relationship_id}
                    className={`group p-3 rounded-xl border transition-all ${
                      dep.health === 'at_risk'
                        ? isDark ? 'border-red-800 bg-red-900/10' : 'border-red-200 bg-red-50/50'
                        : dep.health === 'blocked'
                        ? isDark ? 'border-yellow-800 bg-yellow-900/10' : 'border-yellow-200 bg-yellow-50/50'
                        : dep.health === 'resolved'
                        ? isDark ? 'border-green-800 bg-green-900/10' : 'border-green-200 bg-green-50/50'
                        : isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
                    }`}
                  >
                    {/* Health indicator */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {dep.health === 'at_risk' && (
                          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400">
                            <AlertTriangle className="w-3 h-3" /> AT RISK — predecessor overdue
                          </span>
                        )}
                        {dep.health === 'blocked' && (
                          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-400">
                            <Clock className="w-3 h-3" /> WAITING — predecessor not complete
                          </span>
                        )}
                        {dep.health === 'resolved' && (
                          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400">
                            <CheckCircle className="w-3 h-3" /> RESOLVED
                          </span>
                        )}
                        {dep.health === 'clear' && (
                          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400">
                            <CheckCircle className="w-3 h-3" /> CLEAR — prerequisite done
                          </span>
                        )}
                      </div>
                      <button onClick={() => removeDependency(dep)} disabled={isRemoving}
                        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 transition-all"
                        title="Remove dependency">
                        {isRemoving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unlink className="w-3.5 h-3.5" />}
                      </button>
                    </div>

                    {/* Dependency chain visualization */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                      {/* Target: Must complete first */}
                      <div className={`flex-1 p-2.5 rounded-lg border ${isDark ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            MUST COMPLETE FIRST
                          </span>
                          <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${targetBadge.bg} ${targetBadge.text}`}>
                            {dep.target.item_type.toUpperCase()}
                          </span>
                        </div>
                        <button onClick={() => onOpenItem?.(dep.target.id)}
                          className={`text-sm font-medium text-left hover:text-blue-500 transition-colors ${
                            isTerminal(dep.target.status) ? 'line-through opacity-70' : ''
                          } ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                          {dep.target.title}
                        </button>
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className={`text-[10px] font-mono ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            {dep.target.item_number}
                          </span>
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-full ${ss.bg} ${ss.text}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${ss.dot}`} />
                            {dep.target.status.replace(/_/g, ' ')}
                          </span>
                          {dep.target.due_date && (
                            <span className={`text-[10px] ${
                              daysUntil(dep.target.due_date)! < 0
                                ? 'text-red-500 font-semibold'
                                : isDark ? 'text-gray-500' : 'text-gray-400'
                            }`}>
                              Due {formatDate(dep.target.due_date)}
                            </span>
                          )}
                        </div>
                        {dep.target.assignee_name && (
                          <div className={`flex items-center gap-1 mt-1 text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            <Users className="w-3 h-3" /> {dep.target.assignee_name}
                          </div>
                        )}
                      </div>

                      {/* Arrow */}
                      <div className="flex items-center justify-center px-2">
                        <div className={`flex flex-col items-center gap-0.5 ${isDark ? 'text-gray-600' : 'text-gray-300'}`}>
                          <ArrowRight className="w-5 h-5 hidden sm:block" />
                          <ArrowDown className="w-5 h-5 sm:hidden" />
                          <span className="text-[9px] font-medium">THEN</span>
                        </div>
                      </div>

                      {/* Source: depends on the target */}
                      <div className={`flex-1 p-2.5 rounded-lg border ${isDark ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            CAN THEN START
                          </span>
                          <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${sourceBadge.bg} ${sourceBadge.text}`}>
                            {dep.source.item_type.toUpperCase()}
                          </span>
                        </div>
                        <button onClick={() => onOpenItem?.(dep.source.id)}
                          className={`text-sm font-medium text-left hover:text-blue-500 transition-colors ${
                            isTerminal(dep.source.status) ? 'line-through opacity-70' : ''
                          } ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                          {dep.source.title}
                        </button>
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className={`text-[10px] font-mono ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            {dep.source.item_number}
                          </span>
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-full ${ds.bg} ${ds.text}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${ds.dot}`} />
                            {dep.source.status.replace(/_/g, ' ')}
                          </span>
                          {dep.source.due_date && (
                            <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                              Due {formatDate(dep.source.due_date)}
                            </span>
                          )}
                        </div>
                        {dep.source.assignee_name && (
                          <div className={`flex items-center gap-1 mt-1 text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            <Users className="w-3 h-3" /> {dep.source.assignee_name}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Notes */}
                    {dep.notes && (
                      <div className={`mt-2 px-3 py-1.5 rounded text-xs italic ${isDark ? 'bg-gray-900/30 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
                        {dep.notes}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Summary stats */}
          {depHealth.length > 0 && (
            <div className={`flex items-center gap-4 p-3 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center gap-1.5 text-xs">
                <span className="w-2 h-2 rounded-full bg-green-500" />
                <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                  {depHealth.filter(d => d.health === 'resolved' || d.health === 'clear').length} Clear
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-xs">
                <span className="w-2 h-2 rounded-full bg-yellow-500" />
                <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                  {depHealth.filter(d => d.health === 'blocked').length} Waiting
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-xs">
                <span className="w-2 h-2 rounded-full bg-red-500" />
                <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                  {depHealth.filter(d => d.health === 'at_risk').length} At Risk
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════ MILESTONES SECTION ══════════════════ */}
      {activeSection === 'milestones' && (
        <div className="space-y-3">
          {/* Header + Add Button */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                Project Milestones
              </h3>
              <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Key checkpoints and deliverables on the project timeline
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowAddMilestone(!showAddMilestone)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition-colors">
                <Link2 className="w-3.5 h-3.5" />
                Link Existing
              </button>
              <button onClick={() => setShowCreateMilestone(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors">
                <Plus className="w-3.5 h-3.5" />
                Create New
              </button>
            </div>
          </div>

          {/* Add Milestone Search */}
          {showAddMilestone && (
            <div className={`p-4 rounded-xl border-2 border-dashed ${isDark ? 'border-amber-700 bg-amber-900/10' : 'border-amber-300 bg-amber-50'}`}>
              <div className="flex items-center gap-2 mb-3">
                <Flag className="w-4 h-4 text-amber-500" />
                <span className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Search & Link Milestone
                </span>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" value={milestoneSearch} onChange={(e) => handleMilestoneSearchChange(e.target.value)}
                  placeholder="Search milestones by title or number..."
                  className={`w-full pl-10 pr-3 py-2 text-sm border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                  autoFocus />
              </div>

              {searchingMilestones && (
                <div className="flex items-center gap-2 mt-3 text-sm text-gray-400">
                  <Loader2 className="w-4 h-4 animate-spin" /> Searching...
                </div>
              )}

              {milestoneResults.length > 0 && (
                <div className={`mt-2 border rounded-lg overflow-hidden ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  {milestoneResults.map(ms => {
                    const msStatus = getStatus(ms.status);
                    const isLinking = linkingMilestone === ms.id;
                    return (
                      <div key={ms.id} className={`flex items-center justify-between p-3 ${isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'} transition-colors`}>
                        <div>
                          <div className={`text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                            {ms.title}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className={`text-xs font-mono ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{ms.item_number}</span>
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded-full ${msStatus.bg} ${msStatus.text}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${msStatus.dot}`} />
                              {ms.status}
                            </span>
                            {ms.due_date && (
                              <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                Due {formatDate(ms.due_date)}
                              </span>
                            )}
                          </div>
                        </div>
                        <button onClick={() => linkMilestone(ms.id)} disabled={isLinking}
                          className="flex items-center gap-1 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-xs font-medium rounded-lg transition-colors">
                          {isLinking ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3" />}
                          Link
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {milestoneSearch.length >= 2 && !searchingMilestones && milestoneResults.length === 0 && (
                <p className={`mt-3 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  No milestones found. Create milestones from the main operations view first.
                </p>
              )}

              <div className="flex justify-end mt-3">
                <button onClick={() => { setShowAddMilestone(false); setMilestoneSearch(''); setMilestoneResults([]); }}
                  className={`px-3 py-1.5 text-sm rounded-lg ${isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}>
                  Close
                </button>
              </div>
            </div>
          )}

          {/* Milestone Timeline */}
          {sortedMilestones.length === 0 ? (
            <div className={`text-center py-10 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              <Flag className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No milestones linked</p>
              <p className="text-xs mt-1">Link milestones to track key deliverables and checkpoints</p>
            </div>
          ) : (
            <div className="relative">
              {/* Timeline line */}
              <div className={`absolute left-5 top-0 bottom-0 w-0.5 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />

              <div className="space-y-3">
                {sortedMilestones.map((ms, idx) => {
                  const msStatus = getStatus(ms.status);
                  const days = daysUntil(ms.due_date);
                  const isOverdue = days !== null && days < 0;
                  const isDone = isTerminal(ms.status);
                  const isRemoving = removingId === ms.id;

                  return (
                    <div key={ms.id} className="relative pl-12 group">
                      {/* Timeline dot */}
                      <div className={`absolute left-3.5 top-4 w-3.5 h-3.5 rounded-full border-2 z-10 ${
                        isDone
                          ? 'bg-green-500 border-green-300 dark:border-green-700'
                          : isOverdue
                          ? 'bg-red-500 border-red-300 dark:border-red-700'
                          : 'bg-amber-500 border-amber-300 dark:border-amber-700'
                      }`} />

                      <div className={`p-4 rounded-xl border transition-all ${
                        isDone
                          ? isDark ? 'border-green-800 bg-green-900/10' : 'border-green-200 bg-green-50/50'
                          : isOverdue
                          ? isDark ? 'border-red-800 bg-red-900/10' : 'border-red-200 bg-red-50/50'
                          : isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
                      }`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <button onClick={() => onOpenItem?.(ms.id)}
                                className={`text-sm font-semibold text-left hover:text-amber-500 transition-colors ${
                                  isDone ? 'line-through opacity-70' : ''
                                } ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                                {ms.title}
                              </button>
                              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-full ${msStatus.bg} ${msStatus.text}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${msStatus.dot}`} />
                                {ms.status.replace(/_/g, ' ')}
                              </span>
                            </div>

                            <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                              <span className={`text-xs font-mono ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                {ms.item_number}
                              </span>
                              {ms.due_date && (
                                <span className={`flex items-center gap-1 text-xs ${
                                  isOverdue ? 'text-red-500 font-semibold' : isDark ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                  <CalendarDays className="w-3 h-3" />
                                  {formatDate(ms.due_date)}
                                  {days !== null && !isDone && (
                                    <span className={`ml-1 ${isOverdue ? 'text-red-500' : days <= 7 ? 'text-yellow-500' : ''}`}>
                                      ({isOverdue ? `${Math.abs(days)}d overdue` : `${days}d left`})
                                    </span>
                                  )}
                                </span>
                              )}
                              {ms.assignee_name && (
                                <span className={`flex items-center gap-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                  <Users className="w-3 h-3" /> {ms.assignee_name}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => onOpenItem?.(ms.id)}
                              className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                              title="Open milestone">
                              <ExternalLink className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => unlinkMilestone(ms)} disabled={isRemoving}
                              className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500"
                              title="Unlink milestone">
                              {isRemoving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unlink className="w-3.5 h-3.5" />}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Create Milestone Modal */}
          <WorkItemModal
            isOpen={showCreateMilestone}
            onClose={() => setShowCreateMilestone(false)}
            onSave={handleCreateMilestone}
            itemType="milestone"
            headerColor="from-amber-500 to-amber-600"
            icon={Flag}
            label="Milestone"
            description="Create a new milestone for this project"
          />
        </div>
      )}

      {/* ══════════════════ SCHEDULE SECTION ══════════════════ */}
      {activeSection === 'schedule' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                Auto-Schedule
              </h3>
              <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Automatically compute and push start/due dates based on dependency chains
              </p>
            </div>
            <button
              onClick={autoSchedule}
              disabled={autoScheduling || dependencies.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {autoScheduling ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarDays className="w-4 h-4" />}
              {autoScheduling ? 'Scheduling...' : 'Auto-Schedule'}
            </button>
          </div>

          {/* Schedule overview cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {(() => {
              const withDates = children.filter(c => c.start_date || c.due_date).length;
              const noDates = children.filter(c => !c.start_date && !c.due_date).length;
              const overdue = children.filter(c => {
                if (isTerminal(c.status)) return false;
                const d = daysUntil(c.due_date);
                return d !== null && d < 0;
              }).length;
              const blocked = depHealth.filter(d => d.health === 'blocked').length;

              return (
                <>
                  <div className={`p-3 rounded-xl border ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                    <div className={`text-2xl font-bold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{withDates}</div>
                    <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Scheduled</div>
                  </div>
                  <div className={`p-3 rounded-xl border ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                    <div className={`text-2xl font-bold ${noDates > 0 ? 'text-amber-500' : isDark ? 'text-gray-100' : 'text-gray-900'}`}>{noDates}</div>
                    <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Unscheduled</div>
                  </div>
                  <div className={`p-3 rounded-xl border ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                    <div className={`text-2xl font-bold ${overdue > 0 ? 'text-red-500' : isDark ? 'text-gray-100' : 'text-gray-900'}`}>{overdue}</div>
                    <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Overdue</div>
                  </div>
                  <div className={`p-3 rounded-xl border ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                    <div className={`text-2xl font-bold ${blocked > 0 ? 'text-orange-500' : isDark ? 'text-gray-100' : 'text-gray-900'}`}>{blocked}</div>
                    <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Blocked</div>
                  </div>
                </>
              );
            })()}
          </div>

          {/* How it works */}
          <div className={`p-4 rounded-xl border ${isDark ? 'border-indigo-800 bg-indigo-900/10' : 'border-indigo-200 bg-indigo-50'}`}>
            <h4 className={`text-sm font-medium mb-2 ${isDark ? 'text-indigo-300' : 'text-indigo-700'}`}>How Auto-Schedule Works</h4>
            <ul className={`text-xs space-y-1.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              <li className="flex items-start gap-2"><span className="text-indigo-400 mt-0.5">1.</span> Builds a dependency graph from all "depends_on" and "blocks" relationships</li>
              <li className="flex items-start gap-2"><span className="text-indigo-400 mt-0.5">2.</span> Performs topological sort to determine execution order</li>
              <li className="flex items-start gap-2"><span className="text-indigo-400 mt-0.5">3.</span> Forward pass: each dependent item starts 1 day after its latest prerequisite ends</li>
              <li className="flex items-start gap-2"><span className="text-indigo-400 mt-0.5">4.</span> Duration estimated from "estimated_hours" (8h = 1 day), minimum 1 day</li>
              <li className="flex items-start gap-2"><span className="text-indigo-400 mt-0.5">5.</span> Completed/cancelled items are skipped</li>
            </ul>
          </div>

          {dependencies.length === 0 && (
            <div className={`text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              <GitBranch className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No dependencies defined</p>
              <p className="text-xs mt-1">Switch to the Dependencies tab to create relationships first, then auto-schedule will compute dates.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
